/**
 * Helpers for splitting WordPress paragraph HTML into dropcap / body / endmark regions
 * without treating markup as part of words (avoids `split(" ")` on raw HTML).
 */

/** Normalizes common WP spacing entities before we tokenize or edit segments. */
function normalizeEntities(html: string): string {
  return html.replace(/&nbsp;/gi, " ");
}

/**
 * Strips tags and decodes a small set of common HTML entities so word tokens match reader text.
 *
 * @param html - Fragment HTML from `paragraphContents.html`
 * @returns Plain text with collapsed whitespace
 */
export function stripTagsToPlain(html: string): string {
  const normalized = normalizeEntities(html);
  return normalized
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/(p|div|h\d|li|blockquote)\s*>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&#(\d+);/g, (_, n: string) =>
      String.fromCharCode(Number.parseInt(n, 10)),
    )
    .replace(/&#x([0-9a-f]+);/gi, (_, h: string) =>
      String.fromCharCode(Number.parseInt(h, 16)),
    )
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Word tokens as they would be read (punctuation stays attached, same as `\S+` on plain text).
 *
 * @param html - Fragment HTML from `paragraphContents.html`
 */
export function wordTokensFromHtml(html: string): string[] {
  const plain = stripTagsToPlain(html);
  return plain.match(/\S+/g) ?? [];
}

/**
 * Removes the first plain-text word from the first HTML text segment (not from inside tags).
 *
 * @param html - Fragment HTML; `&nbsp;` is normalized to a space for editing
 */
export function removeFirstWordFromHtml(html: string): string {
  const normalized = normalizeEntities(html);
  const segments = normalized.split(/(<[^>]*>)/g);
  let removed = false;
  const out = segments.map((seg) => {
    if (removed || seg.startsWith("<")) {
      return seg;
    }
    if (/\S/.test(seg)) {
      removed = true;
      /** Keep leading space after the removed word so the next word does not merge with dropcap `rest`. */
      return seg.replace(
        /^(\s*)(\S+)([\s\S]*)$/,
        (_m, ws: string, _word: string, rest: string) => ws + rest,
      );
    }
    return seg;
  });
  return out.join("");
}

/**
 * Removes the last plain-text word from the last HTML text segment (not from inside tags).
 *
 * @param html - Fragment HTML; `&nbsp;` is normalized to a space for editing
 */
export function removeLastWordFromHtml(html: string): string {
  const normalized = normalizeEntities(html);
  const segments = normalized.split(/(<[^>]*>)/g);
  for (let i = segments.length - 1; i >= 0; i -= 1) {
    const seg = segments[i];
    if (!seg || seg.startsWith("<") || !/\S/.test(seg)) {
      continue;
    }
    /** Strip only the last word; keep preceding whitespace so body + endmark span do not merge. */
    const next = seg.replace(/\S+\s*$/, "");
    if (next !== seg) {
      segments[i] = next;
    }
    break;
  }
  return segments.join("");
}

/**
 * First Unicode code point (handles many multi-code-unit characters better than `charAt(0)`).
 *
 * @param s - Non-empty string
 */
export function firstScalarChar(s: string): string {
  const cp = s.codePointAt(0);
  if (cp === undefined) {
    return "";
  }
  return String.fromCodePoint(cp);
}
